import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import PostCard from "@/components/feed/PostCard";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useMessenger } from "@/context/MessengerContext";
import { toast } from "sonner";
import { UserPlus, UserMinus, MouseSimple, Trophy, Users, Newspaper, ChatCircle, X, Globe, ArrowSquareOut, Eye, ThumbsUp } from "@phosphor-icons/react";
import UserBadge from "@/components/UserBadge";
import AchievementBadges from "@/components/AchievementBadges";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

// Hard-cap website names shown on the profile so long titles don't blow out the
// card layout. Longer strings truncate visually AND get an ellipsis mid-string.
const MAX_WEBSITE_NAME_CHARS = 40;
function clampName(s) {
  const str = String(s || "").trim();
  if (str.length <= MAX_WEBSITE_NAME_CHARS) return str;
  return str.slice(0, MAX_WEBSITE_NAME_CHARS - 1).trimEnd() + "…";
}

export default function UserProfile() {
  const { uid } = useParams();
  const { openWindow } = useMessenger();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listModal, setListModal] = useState(null); // "followers" | "following" | null
  const [listData, setListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [{ data: p }, { data: ps }, wsRes] = await Promise.all([
        api.get(`/users/${uid}/profile`),
        api.get(`/users/${uid}/posts`),
        api.get(`/users/${uid}/websites`).catch(() => ({ data: [] })),
      ]);
      setProfile(p);
      setPosts(ps);
      setWebsites(wsRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [uid]);

  async function openList(kind) {
    setListModal(kind);
    setListLoading(true);
    try {
      const { data } = await api.get(`/users/${uid}/${kind}`);
      setListData(data.users || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setListLoading(false);
    }
  }

  async function toggleFollow() {
    if (!profile) return;
    setBusy(true);
    try {
      const endpoint = profile.is_following ? "unfollow" : "follow";
      const { data } = await api.post(`/users/${uid}/${endpoint}`);
      setProfile((p) => ({
        ...p,
        is_following: data.is_following,
        followers_count: p.followers_count + (data.is_following ? 1 : -1),
      }));
      toast.success(data.is_following ? "Now following" : "Unfollowed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  function onDelete(id) {
    setPosts((prev) => prev.filter((p) => p.id !== id));
    setProfile((p) => p ? { ...p, posts_count: Math.max(0, p.posts_count - 1) } : p);
  }
  function onUpdate(next) {
    setPosts((prev) => prev.map((p) => (p.id === next.id ? { ...p, ...next } : p)));
  }

  async function toggleWebsiteLike(w) {
    // Optimistic UI: flip the heart immediately, roll back on error.
    const nextLiked = !w.is_liked_by_me;
    const nextCount = Math.max(0, (w.likes_count || 0) + (nextLiked ? 1 : -1));
    setWebsites((prev) => prev.map((x) => (x.id === w.id ? { ...x, is_liked_by_me: nextLiked, likes_count: nextCount } : x)));
    try {
      const { data } = await api.post(`/websites/${w.id}/like`);
      setWebsites((prev) => prev.map((x) => (x.id === w.id ? { ...x, is_liked_by_me: data.is_liked_by_me, likes_count: data.likes_count } : x)));
    } catch (err) {
      setWebsites((prev) => prev.map((x) => (x.id === w.id ? { ...x, is_liked_by_me: w.is_liked_by_me, likes_count: w.likes_count || 0 } : x)));
      toast.error(err.response?.data?.detail || err.message);
    }
  }

  if (loading) return <DashboardLayout><div className="text-sm text-slate-500">Loading profile…</div></DashboardLayout>;
  if (!profile) return <DashboardLayout><div className="text-sm text-slate-500">User not found.</div></DashboardLayout>;

  const u = profile.user;
  const src = resolveImg(u.avatar_url);

  return (
    <DashboardLayout>
      <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="user-profile-header">
        <div className="h-40 bg-gradient-to-r from-blue-700 via-blue-600 to-green-500" />
        <div className="px-6 pb-6 -mt-12 flex flex-col md:flex-row items-start md:items-end gap-4">
          {src ? (
            <img src={src} alt={u.username} className="h-32 w-32 md:h-40 md:w-40 rounded-full object-cover ring-4 ring-white dark:ring-slate-900" />
          ) : (
            <div className="h-32 w-32 md:h-40 md:w-40 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-5xl md:text-6xl font-black ring-4 ring-white dark:ring-slate-900">
              {(u.username || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0 md:pb-2">
            <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              {u.username}
              <UserBadge user={u} size={26} />
              <AchievementBadges userId={u.id} size={22} />
            </h1>
            {u.full_name && <div className="text-slate-600 dark:text-slate-300">{u.full_name}</div>}
            {u.bio && <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 max-w-xl">{u.bio}</p>}
            <div className="mt-3 flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300 flex-wrap">
              <div className="inline-flex items-center gap-1"><Newspaper size={14} weight="bold" /> <b>{profile.posts_count}</b> posts</div>
              <button
                data-testid="profile-followers-btn"
                onClick={() => openList("followers")}
                className="inline-flex items-center gap-1 hover:text-blue-700 dark:hover:text-blue-400 hover:underline transition-colors duration-200"
              >
                <Users size={14} weight="bold" /> <b>{profile.followers_count}</b> followers
              </button>
              <button
                data-testid="profile-following-btn"
                onClick={() => openList("following")}
                className="inline-flex items-center gap-1 hover:text-blue-700 dark:hover:text-blue-400 hover:underline transition-colors duration-200"
              >
                <Users size={14} weight="bold" /> <b>{profile.following_count}</b> following
              </button>
              <div className="inline-flex items-center gap-1"><Trophy size={14} weight="bold" /> <b>{u.xp || 0}</b> XP</div>
              <div data-testid="profile-sites-surfed" className="inline-flex items-center gap-1" title="Sites surfed"><MouseSimple size={14} weight="bold" /> <b>{Number(u.total_surfs || 0).toLocaleString()}</b> surfed</div>
              {u.membership && u.membership !== "Free" && (
                <span className="inline-flex items-center gap-1 text-yellow-600 font-bold">{u.membership}</span>
              )}
            </div>
          </div>
          {!profile.is_self && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="profile-message-btn"
                onClick={() => openWindow(profile.user)}
                className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 font-bold px-5 py-2 text-sm transition-colors duration-200"
              >
                <ChatCircle size={16} weight="bold" /> Message
              </button>
              <button
                data-testid="profile-follow-btn"
                onClick={toggleFollow}
                disabled={busy}
                className={`inline-flex items-center gap-2 rounded-full font-bold px-5 py-2 text-sm transition-colors duration-200 ${
                  profile.is_following
                    ? "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-red-500 hover:text-white"
                    : "bg-blue-700 hover:bg-blue-600 text-white"
                }`}
              >
                {profile.is_following ? (
                  <><UserMinus size={16} weight="bold" /> Following</>
                ) : (
                  <><UserPlus size={16} weight="bold" /> Follow</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Websites owned — placed under the profile header, above posts */}
      <div className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm" data-testid="profile-websites">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <Globe size={18} weight="fill" className="text-white" />
          </div>
          <h2 className="font-black text-slate-900 dark:text-white text-lg" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            {u?.username
              ? `${u.username.charAt(0).toUpperCase() + u.username.slice(1)}'s Websites`
              : "Websites"} <span className="text-slate-400 font-bold">({websites.length})</span>
          </h2>
        </div>
        {websites.length === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400 py-2">No public websites yet.</div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {websites.map((w) => {
              let host = "";
              try { host = new URL(w.url).hostname.replace(/^www\./, ""); } catch { host = w.url; }
              return (
                <li
                  key={w.id}
                  data-testid={`profile-website-${w.id}`}
                  className="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-white to-slate-50 dark:from-slate-800/40 dark:to-slate-900 p-4 hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-200"
                >
                  {/* Decorative gradient blob */}
                  <div className="pointer-events-none absolute -top-6 -right-6 w-20 h-20 rounded-full bg-gradient-to-br from-blue-200/60 to-indigo-200/40 dark:from-blue-500/10 dark:to-indigo-500/10 blur-xl group-hover:scale-110 transition-transform" />

                  <div className="relative flex items-start gap-3">
                    {/* Globe / world icon */}
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 flex items-center justify-center shadow-md ring-2 ring-white dark:ring-slate-900 group-hover:rotate-[8deg] transition-transform">
                      <Globe size={22} weight="duotone" className="text-white drop-shadow" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <a
                        href={w.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 font-bold text-slate-900 dark:text-white hover:text-blue-700 dark:hover:text-blue-400 text-sm leading-snug group/link max-w-full"
                      >
                        <span className="truncate max-w-[180px]" title={w.title || host}>
                          {clampName(w.title || host)}
                        </span>
                        <ArrowSquareOut size={12} weight="bold" className="shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                      </a>
                      <a
                        href={w.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="mt-0.5 text-[11px] text-blue-600/80 dark:text-blue-400/80 hover:underline block truncate"
                      >
                        {host}
                      </a>

                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center rounded-full bg-slate-900/5 dark:bg-white/10 text-slate-700 dark:text-slate-300 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                          {w.category || "general"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-lime-100 dark:bg-lime-500/15 text-lime-700 dark:text-lime-300 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                          <Eye size={10} weight="bold" />
                          {w.views_earned || 0}
                        </span>
                      </div>
                    </div>

                    {/* Compact thumbs-up like button */}
                    <button
                      data-testid={`profile-website-like-${w.id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWebsiteLike(w); }}
                      aria-label={w.is_liked_by_me ? "Unlike this website" : "Like this website"}
                      title={w.is_liked_by_me ? "You liked this" : "Give this website a like"}
                      className={`shrink-0 self-start inline-flex items-center gap-1 rounded-full px-2.5 py-1 transition-all duration-150 border ${
                        w.is_liked_by_me
                          ? "bg-blue-600 text-white border-blue-700 shadow-sm hover:bg-blue-700"
                          : "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/40 hover:bg-blue-50 dark:hover:bg-blue-500/10 hover:border-blue-400 hover:scale-105"
                      }`}
                    >
                      <ThumbsUp
                        size={14}
                        weight={w.is_liked_by_me ? "fill" : "bold"}
                      />
                      <span className="text-[11px] font-black leading-none">{w.likes_count || 0}</span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-6">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Ads from {u.username}</div>
        {posts.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-sm text-slate-500">
            No posts yet.
          </div>
        )}
        <div className="space-y-4">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} onDelete={onDelete} onUpdate={onUpdate} />
          ))}
        </div>
      </div>

      {/* Followers / Following modal */}
      {listModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setListModal(null)}>
          <div
            data-testid={`user-list-modal-${listModal}`}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-black text-slate-900 dark:text-white text-lg capitalize" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                {listModal} · {listData.length}
              </h3>
              <button onClick={() => setListModal(null)} className="h-8 w-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 inline-flex items-center justify-center">
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {listLoading && <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>}
              {!listLoading && listData.length === 0 && (
                <div className="text-sm text-slate-500 py-6 text-center">No users to show.</div>
              )}
              {listData.map((usr) => {
                const usrImg = resolveImg(usr.avatar_url);
                return (
                  <a
                    key={usr.id}
                    href={`/u/${usr.id}`}
                    data-testid={`list-user-${usr.username}`}
                    onClick={(e) => { e.preventDefault(); setListModal(null); window.location.href = `/u/${usr.id}`; }}
                    className="flex items-center gap-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 px-2 py-2 transition-colors duration-200"
                  >
                    {usrImg ? (
                      <img src={usrImg} alt={usr.username} className="h-10 w-10 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black text-sm shrink-0">
                        {(usr.username || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-slate-900 dark:text-white truncate inline-flex items-center gap-1">
                        {usr.username}
                        <UserBadge user={usr} size={12} />
                      </div>
                      {usr.full_name && <div className="text-xs text-slate-500 truncate">{usr.full_name}</div>}
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
